import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { FLUSH, PAUSE, PERSIST, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'

import { administrationReducer, administrationSlice } from '../features/administration/slice'
import { authReducer, authSlice } from '../features/auth'
import { courseReducer, courseSlice } from '../features/course'
import { participantReducer, participantsSlice } from '../features/participants/slice'
import { tenantReducer, tenantSlice } from '../features/tenant'
import { authApi, AUTH_API_REDUCER_KEY } from '../services/api/auth/api'
import { tenantApi, TENANT_API_REDUCER_KEY } from '../services/api/config/api'
import { RESET_STATE_ACTION_TYPE } from '../store/actions/resetState'

const reducers = {
  [authSlice.name]: authReducer,
  [tenantSlice.name]: tenantReducer,
  [courseSlice.name]: courseReducer,
  [administrationSlice.name]: administrationReducer,
  [participantsSlice.name]: participantReducer,
  [AUTH_API_REDUCER_KEY]: authApi.reducer,
  [TENANT_API_REDUCER_KEY]: tenantApi.reducer,
}

const combinedReducer = combineReducers<typeof reducers>(reducers)

// typeof combinedReducer keeps redux 5's preloaded-state generic, so
// configureStore keeps accepting Partial<RootState> in tests.
export const rootReducer: typeof combinedReducer = (state, action) => {
  if (action.type === RESET_STATE_ACTION_TYPE) {
    state = {} as RootState
  }

  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([authApi.middleware, tenantApi.middleware]),
})

export const persistor = persistStore(store)

export type AppDispatch = typeof store.dispatch
export type AppStore = ReturnType<typeof configureStore>
export type RootState = ReturnType<typeof combinedReducer>
export const useTypedDispatch = () => useDispatch<AppDispatch>()
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector
